import { Button, Checkbox, Collapsible, Icon, Input, Modal, OWCContext, Picker, t, Text, Title } from "owc-react-lib-common";
import { useContext, useState } from "react";
import { View } from 'src/components/View';
import { useAlerts } from "src/hooks/useAlerts";
import * as CompetitionSchemeService from "src/services/CompetitionSchemeService";

enum Type {
  LEAGUE = 1,
  PLAYOFF = 2,
  ONE_ROUND = 4,
}

interface IStepperSchema {
  schema: any | null;
  onClose: () => void
}

interface ISchema {
  id: number;
  name: string;
  phases: ISchemaPhase[];
}

interface ISchemaPhase {
  groups: ISchemaGroup[];
}

interface ISchemaGroup {
  idSchema: number;
  idSchemaGroup?: number;
  idCombinationTable?: number;
  numRounds: number;
  numTeams: number;
  standingVisible: boolean;
  idType: number;
  groupName: string;
  teams: ISchemaTeam[];
}

interface ISchemaTeam {
  idSchemaGroupTeam: number;
  idSchemaGroup: number;
  reference: string;
  idsGroupsCompetitionSchema: string;
  position: number;
  bestof: number;
}

interface ICreateGroupsModal {
  phaseIdx: number;
  groupAmount: number;
  teamAmount: number;
  roundAmount: number;
  standingVisible: boolean;
  typeId: number;
}

interface ICreateFicticiousTeamModal {
  phaseIdx: number;
  groupIdx: number;
  idsGroupsCompetitionSchema?: number;
  position?: number;
}

export const StepperSchema = ({ onClose }: IStepperSchema) => {
  const { colors } = useContext(OWCContext);
  const { createDSMError } = useAlerts();
  const [createGroupsModal, setCreateGroupsModal] = useState<ICreateGroupsModal>();
  const [ficticiousTeamModal, setFicticiousTeamModal] = useState<ICreateFicticiousTeamModal>();
  const [state, setState] = useState<ISchema>({
    id: undefined,
    name: "",
    phases: [],
  });

  /**
   * save schema
   */
  const create = async () => {
    try {
      const response = await CompetitionSchemeService.createScheme({
        name: state?.name,
      });

      setState(prev => ({
        ...prev,
        id: response.data.id,
      }));

      addPhase();
      // onSave?.();
    } catch (error) {
      createDSMError(error);
    }
  }

  /**
   * add phase
   */
  const addPhase = () => {
    setState(prev => ({
      ...prev, phases: [...prev.phases, {
        groups: [],
      }]
    }))
  }

  /**
   * delete phase
   */
  const deletePhase = (phaseIdx: number) => {
    setState(prev => ({
      ...prev,
      phases: prev.phases.filter((_, idx) => idx !== phaseIdx)
    }));
  }

  /**
   * open group modal
   */
  const openCreateGroupsModal = (phaseIdx: number) => {
    setCreateGroupsModal({
      phaseIdx,
      groupAmount: 1,
      teamAmount: 4,
      roundAmount: 1,
      standingVisible: true,
      typeId: Type.LEAGUE,
    })
  }

  /**
   * create groups
   */
  const createGroups = async () => {
    try {
      const response = await Promise.all((Array.from(new Array(createGroupsModal.groupAmount), (_, idx) => (
        CompetitionSchemeService.createSchemeGroup(state.id, {
          groupName: `${t("organizer.schema.group").toUpperCase()} ${state.phases[createGroupsModal.phaseIdx].groups.length + idx + 1}`,
          idSchema: state.id,
          idType: createGroupsModal.typeId,
          numRounds: createGroupsModal.roundAmount,
          numTeams: createGroupsModal.teamAmount,
          standingVisible: createGroupsModal.standingVisible,
          idCombinationTable: null,
          idSchemaGroup: null,
          teams: [],
        })
      ))));

      // update state
      setState(prev => ({
        ...prev,
        phases: prev.phases.map((phase, idx) => idx !== createGroupsModal.phaseIdx ? phase : {
          ...phase,
          groups: [
            ...phase.groups,
            ...response.map((response) => ({ ...response.data, teams: [] })),
          ]
        })
      }));

      setCreateGroupsModal(undefined);
    } catch (error) {
      createDSMError(error);
    }
  }

  /**
   * on change group
   */
  const onChangeGroup = (phaseIdx: number, groupIdx: number, field: string, value: string | number | boolean) => {
    setState(prev => ({
      ...prev,
      phases: prev.phases.map((phase, idx) => idx !== phaseIdx ? phase : {
        ...phase,
        groups: phase.groups.map((group, idx) => idx !== groupIdx ? group : {
          ...group,
          [field]: value,
        }),
      })
    }));
  }

  /**
   * save group
   */
  const saveGroup = async (group: ISchemaGroup) => {
    try {
      await CompetitionSchemeService.createSchemeGroup(state.id, group);
    } catch (error) {
      createDSMError(error);
    }
  }

  /**
   * delete group
   */
  const deleteGroup = async (phaseIdx: number, groupIdx: number) => {
    try {
      await CompetitionSchemeService.deleteSchemeGroup(state.id, state.phases[phaseIdx].groups[groupIdx]);

      // update state
      setState(prev => ({
        ...prev,
        phases: prev.phases.map((phase, idx) => idx !== phaseIdx ? phase : {
          ...phase,
          groups: phase.groups.filter((_, idx) => idx !== groupIdx),
        })
      }));
    } catch (error) {
      createDSMError(error);
    }
  }

  /**
   * open ficticious team modal
   */
  const openFicticiousTeamModal = (phaseIdx: number, groupIdx: number) => {
    setFicticiousTeamModal({ phaseIdx, groupIdx })
  }

  /**
   * create ficticious team
   */
  const createFicticiousTeam = async () => {
    try {
      const response = await CompetitionSchemeService.createSchemeGroupTeam(state.phases[ficticiousTeamModal?.phaseIdx].groups[ficticiousTeamModal?.groupIdx].idSchemaGroup, ficticiousTeamModal);

      // update state
      setState(prev => ({
        ...state,
        phases: prev.phases.map((phase, idx) => idx !== ficticiousTeamModal.phaseIdx ? phase : {
          ...phase,
          groups: phase.groups.map((group, idx) => idx !== ficticiousTeamModal.groupIdx ? group : {
            ...group,
            teams: [...group.teams, response.data],
          })
        })
      }));

      setFicticiousTeamModal(undefined);
    } catch (error) {
      createDSMError(error);
    }
  }

  /**
   * delete ficticious team
   */
  const deleteTeam = async (phaseIdx: number, groupIdx: number, teamIdx: number) => {
    try {
      await CompetitionSchemeService.deleteSchemaTeam(state.phases[phaseIdx].groups[groupIdx].teams[teamIdx]);

      // update state
      setState(prev => ({
        ...state,
        phases: prev.phases.map((phase, idx) => idx !== phaseIdx ? phase : {
          ...phase,
          groups: phase.groups.map((group, idx) => idx !== groupIdx ? group : {
            ...group,
            teams: group.teams.filter((_, idx) => idx !== teamIdx),
          })
        })
      }));
    } catch(error) {
      createDSMError(error);
    }
  }

  /**
   * render
   */
  const types = {
    [Type.LEAGUE]: t("organizer.schema.league"),
    [Type.ONE_ROUND]: t("organizer.schema.oneRound"),
    [Type.PLAYOFF]: t("organizer.schema.playoff"),
  }

  return (
    <View>
      <Input onChange={(value) => setState(prev => ({ ...prev, name: value }))} />

      {state.phases.map((phase, phaseIdx) => (
        <Collapsible key={phaseIdx} title={`${t("organizer.schema.phase")} ${phaseIdx + 1}`} defaultOpen headerRight={<Icon type="trash" color="white" containerStyle={{ borderWidth: 1, borderColor: "white" }} onPress={() => deletePhase(phaseIdx)} />} contentStyle={{ gap: 15 }}>
          <Button text={t("organizer.schema.addGroups")} onPress={() => openCreateGroupsModal(phaseIdx)} marginBottom={0} />

          <View style={{ gap: 10, flexDirection: "row", flexWrap: "wrap" }}>
            {phase.groups.map((group, groupIdx) => (
              <View style={{ borderWidth: 1, borderColor: colors.primary.background, padding: 15, backgroundColor: "white", gap: 15, maxWidth: 500, width: "100%" }}>
                <View style={{ /*flexDirection: "row", alignItems: "center", */gap: 5 }}>
                  <Title size="small" text={types[group.idType]} marginBottom={0} />
                  <Input label={t("organizer.schema.name")} value={group.groupName} onChange={(value) => onChangeGroup(phaseIdx, groupIdx, "groupName", value)} containerStyle={{ marginBottom: 0 }} />
                  <Input label={t("organizer.schema.teamAmount")} value={group.numTeams} onChange={(value) => onChangeGroup(phaseIdx, groupIdx, "numTeams", parseInt(value))} containerStyle={{ marginBottom: 0 }} />
                  <Input label={t("organizer.schema.roundAmount")} value={group.numRounds} onChange={(value) => onChangeGroup(phaseIdx, groupIdx, "numRounds", parseInt(value))} containerStyle={{ marginBottom: 0 }} />
                </View>

                <Checkbox label={t("organizer.schema.standingVisible")} value={group.standingVisible} onChange={(value) => onChangeGroup(phaseIdx, groupIdx, "standingVisible", value)} containerStyle={{ marginBottom: 0 }} />

                <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                  <Button type="success" text={t("global.save")} onPress={() => saveGroup(group)} containerStyle={{ borderColor: "transparent", marginBottom: 0, flex: 1 }} />
                  <Button type="danger" text={t("global.delete")} onPress={() => deleteGroup(phaseIdx, groupIdx)} containerStyle={{ borderColor: "transparent", marginBottom: 0, flex: 1 }} />
                  <Button text={t("organizer.schema.addFicticiousTeam")} onPress={() => openFicticiousTeamModal(phaseIdx, groupIdx)} containerStyle={{ borderColor: "transparent", marginBottom: 0, flex: 1 }} disabled={group.teams?.length >= group.numTeams} />
                </View>

                {!!group.teams?.length && (
                  <View style={{ maxWidth: 460 }}>
                    {group.teams?.map((team, teamIdx) => (
                      <View style={{ flexDirection: "row", alignItems: "center", borderTopWidth: teamIdx === 0 ? 1 : 0, borderBottomWidth: 1, borderColor: "#eee", paddingVertical: 5, backgroundColor: teamIdx >= group.numTeams ? "rgba(255, 0, 0, 0.2)" : undefined }}>
                        <View style={{ flex: 1 }}>
                          <Text>{state.phases.flatMap((phase) => phase.groups).find((group) => group.idSchemaGroup === parseInt(team.idsGroupsCompetitionSchema))?.groupName}</Text>
                          <Text>{t("organizer.schema.position")} {team.position}</Text>
                        </View>

                        <Icon type="trash" color="crimson" onPress={() => deleteTeam(phaseIdx, groupIdx, teamIdx)} />
                      </View>
                    ))}
                  </View>
                )}

                <Modal visible={!!ficticiousTeamModal} title={t("organizer.schema.addFicticiousTeam")} close={() => setFicticiousTeamModal(undefined)}>
                  <Picker
                    label={t("organizer.schema.group")}
                    selected={ficticiousTeamModal?.idsGroupsCompetitionSchema}
                    items={state.phases.filter((_, idx) => idx <= ficticiousTeamModal?.phaseIdx).flatMap((phase) => phase.groups.filter((group) => group.idSchemaGroup !== state.phases[ficticiousTeamModal?.phaseIdx].groups[ficticiousTeamModal?.groupIdx].idSchemaGroup).map(group => ({ label: group.groupName, value: group.idSchemaGroup })))}
                    onChange={(value) => setFicticiousTeamModal(prev => ({ ...prev, idsGroupsCompetitionSchema: parseInt(value) }))}
                  />

                  <Picker
                    label={t("organizer.schema.position")}
                    items={Array.from(new Array(state.phases.flatMap(phase => phase.groups).find(group => group.idSchemaGroup === ficticiousTeamModal?.idsGroupsCompetitionSchema)?.numTeams), (_, idx) => ({ label: idx + 1, value: idx + 1 }))}
                    onChange={(value) => setFicticiousTeamModal(prev => ({ ...prev, position: parseInt(value) }))}
                  />

                  <Button type="success" text={t("global.save")} onPress={createFicticiousTeam} marginBottom={0} />
                </Modal>
              </View>
            ))}
          </View>
        </Collapsible>
      ))}

      {!state?.id ? (
        <Button type="success" text={t("global.save")} onPress={create} marginBottom={0} />
      ) : (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
          <Button text={t("organizer.schema.addPhase")} onPress={addPhase} marginBottom={0} />
          <Button type="success" text={t("global.save")} onPress={onClose} marginBottom={0} />
        </View>
      )}

      <Modal visible={!!createGroupsModal} title={t("organizer.schema.addGroups")} close={() => setCreateGroupsModal(undefined)}>
        <Picker
          label={t("organizer.schema.type")}
          defaultValue={createGroupsModal?.typeId}
          onChange={(value) => setCreateGroupsModal(prev => ({ ...prev, typeId: parseInt(value) }))}
          containerStyle={{ flex: 1 }}
          items={[
            { value: Type.LEAGUE, label: types[Type.LEAGUE] },
            { value: Type.ONE_ROUND, label: types[Type.ONE_ROUND] },
            { value: Type.PLAYOFF, label: types[Type.PLAYOFF] },
          ]}
        />

        <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
          <Input label={t("organizer.schema.groupAmount")} defaultValue={createGroupsModal?.groupAmount} onChange={(value) => setCreateGroupsModal(prev => ({ ...prev, groupAmount: parseInt(value) }))} containerStyle={{ flex: 1 }} />
          <Input label={t("organizer.schema.teamAmount")} defaultValue={createGroupsModal?.teamAmount} onChange={(value) => setCreateGroupsModal(prev => ({ ...prev, teamAmount: parseInt(value) }))} containerStyle={{ flex: 1 }} />
          <Input label={t("organizer.schema.roundAmount")} defaultValue={createGroupsModal?.roundAmount} onChange={(value) => setCreateGroupsModal(prev => ({ ...prev, roundAmount: parseInt(value) }))} containerStyle={{ flex: 1 }} />
        </View>

        <Checkbox label={t("organizer.schema.standingVisible")} defaultValue={createGroupsModal?.standingVisible} onChange={(value) => setCreateGroupsModal(prev => ({ ...prev, standingVisible: value }))} />

        <Button text={t("global.save")} onPress={createGroups} marginBottom={0} />
      </Modal>

    </View>
  );
};